import axios, { AxiosError } from "axios";

import {
  ITrackerStatus,
  ReleaseTrackersFromMaintenanceResultCodeEnum,
  SendTrackersToMaintenanceResultCodeEnum,
  TrackerElevation,
  SendTrackersToFreezeResultCodeEnum,
  ResetTrackersResultCodeEnum,
  HomingDirection,
  SendTrackersToHomingResultCodeEnum,
  ITrackersCommandResultsMap,
  ITrackersCommandResponse,
  ForceTrackersElevationResultCodeEnum,
  McsCommandNotImplementedStatusCode,
  SendTrackersToMaxElevationResultCodeEnum,
  RecalibrateTrackersElevationResultCodeEnum,
  SiteDesignMetadata,
  AuxDevicesStatus,
} from "./TrackersModels";
import { ReplacementType, IInitiateReplacementResponse } from "./ReplacementCommand/ReplacementModels";
import APP_CONFIG from "../app/configuration/AppConfig";

export interface MapBackgroundImage {
  blob: Blob;
  localBounds: { minEast: number; maxEast: number; minNorth: number; maxNorth: number };
  projection?: { gridSize: number; textureCoordinates: number[] };
}

export async function getAerialMapBackgroundAsync(
  siteId: string,
  signal?: AbortSignal
): Promise<MapBackgroundImage> {
  const url = `${APP_CONFIG.serviceUrls.apiHostUrl}/SiteMetadata/${encodeURIComponent(siteId)}/map-background/aerial`;
  const response = await axios.get<Blob>(url, { responseType: "blob", signal });
  const readBound = (name: string) => {
    const value = Number(response.headers[name]);
    if (!Number.isFinite(value)) {
      throw new Error("Map background response did not include valid local bounds.");
    }
    return value;
  };
  const projectionGridSize = response.headers["x-soma-map-projection-grid-size"];
  const projectionCoordinates = response.headers["x-soma-map-projection-texture-coordinates"];
  let projection: MapBackgroundImage["projection"];
  if (projectionGridSize !== undefined || projectionCoordinates !== undefined) {
    const gridSize = Number(projectionGridSize);
    const textureCoordinates = String(projectionCoordinates)
      .split(",")
      .map(Number);
    if (
      !Number.isInteger(gridSize) ||
      gridSize < 2 ||
      textureCoordinates.length !== gridSize * gridSize * 2 ||
      textureCoordinates.some((coordinate) => !Number.isFinite(coordinate) || coordinate < 0 || coordinate > 1)
    ) {
      throw new Error("Map background response included an invalid image projection.");
    }
    projection = { gridSize, textureCoordinates };
  }
  return {
    blob: response.data,
    localBounds: {
      minEast: readBound("x-soma-map-min-east"),
      maxEast: readBound("x-soma-map-max-east"),
      minNorth: readBound("x-soma-map-min-north"),
      maxNorth: readBound("x-soma-map-max-north"),
    },
    ...(projection ? { projection } : {}),
  };
}

export async function getAllLatestTrackersAsync(siteId: string): Promise<ITrackerStatus[]> {
  const url = `${APP_CONFIG.serviceUrls.apiHostUrl}/TrackerData/getTrackersData`;
  const params = { siteId: siteId };
  const httpResponse = await axios.request<ITrackerStatus[]>({
    url: url,
    params: params,
  });
  return httpResponse.data;
}

export async function markReachedCommissionElevation(
  siteId: string,
  deviceId: number,
  direction: HomingDirection
) {
  const url = `${APP_CONFIG.serviceUrls.apiHostUrl}/site/${siteId}/trackers/commission/mark-reached-commission-elevation`;
  const params = {
    DeviceId: deviceId,
    Direction: direction,
  };
  return await axios.post<void>(url, params);
}

export async function resetReachedCommissionElevation(
  siteId: string,
  deviceId: number,
  direction: HomingDirection
) {
  const url = `${APP_CONFIG.serviceUrls.apiHostUrl}/site/${siteId}/trackers/commission/reset-reached-commission-elevation`;
  const params = {
    DeviceId: deviceId,
    Direction: direction,
  };
  return await axios.post<void>(url, params);
}

export async function sendTrackersToMaintenance(
  siteId: string,
  trackersIds: number[],
  targetElevationZenith: number,
  reason: string
) {
  const url = `${APP_CONFIG.serviceUrls.apiHostUrl}/site/${siteId}/trackers/commands/maintenance`;
  const params = {
    TargetElevationDegrees: targetElevationZenith, //https://solargik.atlassian.net/browse/C9-2602 need to change to targetElevationZenith in BFF
    TrackersList: trackersIds,
    Reason: reason,
  };
  return await sendCommand(
    trackersIds,
    url,
    params,
    SendTrackersToMaintenanceResultCodeEnum.UnexpectedError
  );
}

export async function sendTrackersToMaxElevation(
  siteId: string,
  trackersIds: number[],
  direction: HomingDirection
) {
  const url = `${APP_CONFIG.serviceUrls.apiHostUrl}/site/${siteId}/trackers/commands/sendToMaxElevation`;
  const params = {
    Direction: direction,
    TrackersList: trackersIds,
  };
  return await sendCommand(
    trackersIds,
    url,
    params,
    SendTrackersToMaxElevationResultCodeEnum.UnexpectedError
  );
}

export async function recalibrateTrackersElevation(
  siteId: string,
  trackersIds: number[],
  targetElevation: TrackerElevation
) {
  const url = `${APP_CONFIG.serviceUrls.apiHostUrl}/site/${siteId}/trackers/commands/recalibrateElevation`;
  const params = {
    TrackersList: trackersIds,
    TargetElevationDegrees: targetElevation.value * (targetElevation.direction === "West" ? 1 : -1),
  };
  return await sendCommand(
    trackersIds,
    url,
    params,
    RecalibrateTrackersElevationResultCodeEnum.UnexpectedError
  );
}

export async function releaseTrackersFromMaintenance(siteId: string, trackersIds: number[]) {
  const url = `${APP_CONFIG.serviceUrls.apiHostUrl}/site/${siteId}/trackers/commands/release`;
  const params = { TrackersList: trackersIds };
  return await sendCommand(
    trackersIds,
    url,
    params,
    ReleaseTrackersFromMaintenanceResultCodeEnum.UnexpectedError
  );
}

export async function sendTrackersToFreeze(siteId: string, trackersIds: number[], reason: string) {
  const url = `${APP_CONFIG.serviceUrls.apiHostUrl}/site/${siteId}/trackers/commands/freeze`;
  const params = { TrackersList: trackersIds, Reason: reason };
  return await sendCommand(trackersIds, url, params, SendTrackersToFreezeResultCodeEnum.UnexpectedError);
}

export const resetTrackers = async (siteId: string, trackersIds: number[]) => {
  const url = `${APP_CONFIG.serviceUrls.apiHostUrl}/site/${siteId}/trackers/commands/reset`;
  const params = { TrackersList: trackersIds };
  return await sendCommand(trackersIds, url, params, ResetTrackersResultCodeEnum.UnexpectedError);
};

export async function sendTrackersToHoming(
  siteId: string,
  trackersIds: number[],
  homingDirection: HomingDirection
) {
  const url = `${APP_CONFIG.serviceUrls.apiHostUrl}/site/${siteId}/trackers/commands/homing`;
  const params = {
    TrackersList: trackersIds,
    HomingDirection: homingDirection,
  };
  return await sendCommand(trackersIds, url, params, SendTrackersToHomingResultCodeEnum.UnexpectedError);
}

export async function forceTrackersElevation(
  siteId: string,
  trackersIds: number[],
  targetElevation: TrackerElevation
) {
  const url = `${APP_CONFIG.serviceUrls.apiHostUrl}/site/${siteId}/trackers/commands/forceElevation`;
  const params = {
    TargetElevationDegrees: targetElevation.value * (targetElevation.direction === "West" ? 1 : -1),
    TrackersList: trackersIds,
  };
  return await sendCommand(
    trackersIds,
    url,
    params,
    ForceTrackersElevationResultCodeEnum.UnexpectedError
  );
}

export async function initiateTrackerComponentReplacement(
  siteId: string,
  trackerId: number,
  componentType: ReplacementType,
  serialNumber: string,
  reason: string
): Promise<IInitiateReplacementResponse> {
  const url = `${APP_CONFIG.serviceUrls.apiHostUrl}/site/${siteId}/trackers/${trackerId}/component-replacements`;
  const params = {
    ComponentType: componentType,
    SerialNumber: serialNumber,
    Reason: reason,
  };
  const httpResponse = await axios.post<IInitiateReplacementResponse>(url, params);
  return httpResponse.data;
}

async function sendCommand(
  trackersIds: number[],
  url: string,
  params: unknown,
  unexpectedErrorValue: number
): Promise<ITrackersCommandResultsMap> {
  try {
    const httpResponse = await axios.post<ITrackersCommandResponse>(url, params);
    return httpResponse.data.results;
  } catch (error) {
    const errorValue =
      (error as AxiosError).response?.status === 501
        ? McsCommandNotImplementedStatusCode
        : unexpectedErrorValue;
    return Object.fromEntries(trackersIds.map((id) => [id, errorValue]));
  }
}

export async function getSiteDesignMetadata(
  siteId: string,
  signal?: AbortSignal
): Promise<SiteDesignMetadata> {
  const url = `${APP_CONFIG.serviceUrls.apiHostUrl}/SiteMetadata/${encodeURIComponent(siteId)}/design-metadata`;
  const httpResponse = await axios.get<SiteDesignMetadata>(url, { signal });
  return httpResponse.data;
}

export async function getAuxStatus(siteId: string, signal?: AbortSignal): Promise<AuxDevicesStatus> {
  const url = `${APP_CONFIG.serviceUrls.apiHostUrl}/SiteStatus/getAuxStatus`;
  const httpResponse = await axios.get<AuxDevicesStatus>(url, {
    params: { siteId },
    signal,
  });
  return httpResponse.data;
}
